import { useCallback, useEffect, useRef, useState } from "react";
import { Video, RotateCcw, Trash2, AlertCircle, Settings2, X } from "lucide-react";
import { videoConfig, listVideos, deleteVideo, STATUS } from "@/lib/videoApi";
import { useUploads, useScopeVersion, addUpload, cancelUpload, resumeUpload, STALL_MS } from "@/lib/uploadManager";
import { DropZone, splitFiles, fmtSize } from "@/components/apply/DropZone";
import { StreamThumb } from "@/components/video/StreamThumb";
import { ConfirmDelete } from "@/components/adtemplate/ConfirmDelete";
import { VideoDownloadButton } from "@/components/video/VideoDownload";

const EXTS = ["mp4", "mov", "m4v"];
const ACCEPT = "video/mp4,video/quicktime,video/x-m4v,.mp4,.mov,.m4v";
const mins = (s) => (s >= 60 ? `${Math.round(s / 60)} minutes` : `${s} seconds`);

export function useTick(on, ms = 5000) {
  const [, setT] = useState(0);
  useEffect(() => {
    if (!on) return undefined;
    const i = setInterval(() => setT((t) => t + 1), ms);
    return () => clearInterval(i);
  }, [on, ms]);
}
export const liveStatus = (x) => (x.status === "uploading" && Date.now() - x.lastProgressAt > STALL_MS ? "stalled" : x.status);

function Pill({ s, pct }) {
  const [l, c] = STATUS[s] || STATUS.waiting;
  return <span data-testid="video-status" className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${c}`}>{l}{(s === "uploading" || s === "stalled") && pct != null ? ` — ${pct}%` : ""}</span>;
}

const HINT = {
  waiting: "Checking the video before upload…",
  stalled: "Upload stalled — check your connection. It retries automatically, or tap Retry.",
  processing: "Cloudflare received the file and is preparing it. You can keep going.",
  interrupted: "Upload interrupted (the page was closed or refreshed). Tap Retry and choose the same video — it continues where it stopped.",
};

function Row({ v, token, readOnly, onRetry, onRemove, horseId }) {
  const s = v.local ? liveStatus(v) : v.status === "uploading" ? "interrupted" : v.status;
  const pill = s === "interrupted" ? "failed" : s;
  return (
    <li data-testid={`video-row-${v.id || v.key}`} className="flex flex-wrap sm:flex-nowrap items-center gap-3 rounded-lg border border-stone-200 bg-white p-2">
      <div className="w-28 shrink-0 rounded overflow-hidden">
        {v.id && s === "ready" ? <StreamThumb videoId={v.id} token={token} name={v.original_filename} className="aspect-video" />
          : <div className="aspect-video flex items-center justify-center bg-stone-800 text-stone-300"><Video className="h-5 w-5" /></div>}
      </div>
      <div className="min-w-[140px] flex-1 text-sm">
        <div className="font-medium text-stone-900 truncate">{v.original_filename || v.name}</div>
        <div className="flex flex-wrap items-center gap-1.5 mt-0.5"><Pill s={pill} pct={v.progress} /><span className="text-xs text-stone-500">{fmtSize(v.size || 0)}{v.duration ? ` · ${Math.round(v.duration)}s` : ""}</span></div>
        {v.local && (s === "uploading" || s === "stalled") && <div className="mt-1 h-1.5 rounded bg-stone-200 overflow-hidden"><div data-testid="video-progress-bar" className="h-full bg-amber-600 transition-[width]" style={{ width: `${v.progress || 0}%` }} /></div>}
        {HINT[s] && <p data-testid="video-hint" className="mt-1 text-xs text-stone-500">{HINT[s]}</p>}
        {v.error && s === "failed" && <p className="mt-1 text-xs text-red-600 flex items-center gap-1"><AlertCircle className="h-3.5 w-3.5 shrink-0" />{v.error}</p>}
      </div>
      {!readOnly && ["failed", "stalled", "interrupted"].includes(s) && <button type="button" data-testid="video-retry" aria-label="Retry upload" onClick={onRetry} className="h-11 w-11 rounded-full border border-stone-300 flex items-center justify-center shrink-0"><RotateCcw className="h-4 w-4" /></button>}
      {horseId && v.id && !v.local && s === "ready" && <VideoDownloadButton horseId={horseId} videoKey={v.id} testId={`video-download-${v.id}`} />}
      {!readOnly && s !== "waiting" && <button type="button" data-testid="video-remove" aria-label={v.local && s !== "failed" ? "Cancel upload" : "Delete video"} onClick={onRemove} className="h-11 w-11 rounded-full border border-stone-300 text-red-700 flex items-center justify-center shrink-0">{v.local && s !== "failed" ? <X className="h-4 w-4" /> : <Trash2 className="h-4 w-4" />}</button>}
    </li>
  );
}

// scope: {type: "app"|"horse", id, token?}. Uploads run in the shared upload manager, so leaving this screen never stops them.
export function VideoUploader({ scope, readOnly = false, adminView = false, onChanged, title = "Videos" }) {
  const [cfg, setCfg] = useState(null);
  const [rows, setRows] = useState([]);
  const [notice, setNotice] = useState(null);
  const [del, setDel] = useState(null);
  const retryFor = useRef(null);
  const picker = useRef(null);
  const local = useUploads(scope.id);
  const version = useScopeVersion(scope.id);
  const load = useCallback(() => listVideos(scope).then(setRows).catch(() => {}), [scope.type, scope.id, scope.token]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { videoConfig().then(setCfg).catch(() => setCfg({ configured: false })); }, []);
  useEffect(() => { load(); if (version) onChanged?.(); }, [load, version]); // eslint-disable-line react-hooks/exhaustive-deps
  const busy = rows.some((v) => v.status === "processing") || local.length > 0;
  useTick(local.some((x) => x.status === "uploading"));
  useEffect(() => {
    if (!busy) return undefined;
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [busy, load]);

  const add = async (files) => {
    const retryId = retryFor.current;
    retryFor.current = null;
    const { ok, error } = splitFiles(files, EXTS, "Videos must be MP4 or MOV.");
    const msgs = error ? [error] : [];
    const localIds = new Set(local.map((x) => x.videoId).filter(Boolean));
    let room = cfg.max_videos - rows.filter((r) => !localIds.has(r.id)).length - local.length + (retryId ? 1 : 0);
    for (const f of ok) {
      if (f.size > cfg.max_bytes) { msgs.push(`${f.name} is larger than ${fmtSize(cfg.max_bytes)}. Please trim or compress it.`); continue; }
      if (room <= 0) { msgs.push(`You can add up to ${cfg.max_videos} videos.`); break; }
      room -= 1;
      const r = await addUpload(scope, f, { retryId, maxSeconds: cfg.max_duration_seconds });
      if (r.error) { msgs.push(r.error); room += 1; }
    }
    setNotice(msgs.join(" ") || null);
  };

  const remove = async () => {
    const v = del; setDel(null);
    try {
      if (v.local) await cancelUpload(v.key, scope.token);
      else { await deleteVideo(v.id, scope.token); await load(); onChanged?.(); }
    } catch (e) { setNotice(e.message); }
  };

  if (!cfg) return null;
  const liveIds = new Set(local.map((x) => x.videoId).filter(Boolean));
  const shown = [...rows.filter((r) => !liveIds.has(r.id)), ...local.map((x) => ({ ...x, local: true }))];
  return (
    <section id="video-uploader" data-testid="video-uploader" className="rounded-xl border border-stone-200 bg-stone-50 p-4 scroll-mt-4">
      <h3 className="font-semibold text-stone-900 flex items-center gap-2"><Video className="h-5 w-5 text-amber-800" /> {title}</h3>
      {!cfg.configured ? (
        <p data-testid="video-not-configured" className="mt-2 text-sm text-stone-600 flex items-start gap-2">
          {adminView ? <><Settings2 className="h-4 w-4 mt-0.5 shrink-0 text-amber-800" />Video uploads need Cloudflare Stream. Add the Cloudflare Account ID and API Token in Settings › Video Uploads to switch them on.</> : "Video upload isn't available yet. Submit your photos for now — you can add videos later."}
        </p>
      ) : !readOnly && (
        <>
          <p className="mt-2 mb-3 text-sm text-stone-600">MP4 or iPhone MOV · up to {cfg.max_videos} videos, {fmtSize(cfg.max_bytes)} and {mins(cfg.max_duration_seconds)} each. Uploads keep going while you move through the other steps — just keep this page open.</p>
          <DropZone testId="video" accept={ACCEPT} onFiles={add} icon={Video} label="Add Videos" dropLabel="Drop Videos Here" />
          <input ref={picker} type="file" accept={ACCEPT} className="hidden" data-testid="video-retry-input" onChange={(e) => { add(e.target.files); e.target.value = ""; }} />
        </>
      )}
      {notice && <p data-testid="video-notice" role="alert" className="mt-2 text-sm font-medium text-red-600">{notice}</p>}
      {shown.length > 0 && (
        <ul data-testid="video-list" className="mt-3 space-y-2">
          {shown.map((v) => <Row key={v.local ? v.key : v.id} v={v} token={scope.token} horseId={scope.type === "horse" ? scope.id : null} readOnly={readOnly || !cfg.configured}
            onRetry={() => { if (v.local && resumeUpload(v.key)) return; retryFor.current = v.local ? v.videoId : v.id; picker.current?.click(); }} onRemove={() => setDel(v)} />)}
        </ul>
      )}
      {readOnly && !shown.length && <p data-testid="video-none" className="mt-2 text-sm text-stone-500">No videos uploaded.</p>}
      <ConfirmDelete item={del && { name: del.original_filename || del.name }} kind="video" onCancel={() => setDel(null)} onConfirm={remove}
        note={del?.local ? "This stops the upload and removes the video." : "This permanently deletes the video. To keep it but leave it out of the ad, use the eye button in Advertisement Order instead."} />
    </section>
  );
}
